import { FC } from "react"
import classNames from "classnames"
import { safelyRenderHtml } from "@/util/safelyRenderHtml"
import { useGeoDataEffect } from "../../hooks/useGeoData"
import { PREMIUM_HERO_FORM_ATF_LAYOUT, PremiumHeroForm } from "./hero-form"
import YoutubeEmbedATF from "@/storyblok/sections/YoutubeEmbed_ATF"

export interface IPetsHeroATFYoutube {
  blok: {
    title: string
    description: string
    buttonLink?: string
    buttonLabel?: string
    geoPostDescription?: string
    geoTitle?: string
    hasGeoCustomization?: boolean
    hasSwapCustomization?: boolean
    hasListingForm?: boolean
    animalType?: string
    bgImagePlacement?: string
    embedUrl?: string
  }
}

// The desktop video copy is shown from Tailwind's `md` breakpoint up.
const DESKTOP_VIDEO_MEDIA = "(min-width: 768px)"

const PetsHeroATFYoutube: FC<IPetsHeroATFYoutube> = (props) => {
  const {
    title,
    description,
    buttonLabel,
    buttonLink,
    hasGeoCustomization = false,
    hasSwapCustomization = false,
    geoPostDescription = "Hire top-rated transporters in {location}.",
    geoTitle = "",
    hasListingForm = false,
    embedUrl,
  } = props.blok
  // Using CSS-only responsive rendering to avoid hydration mismatch
  const { geoDescription, geoSessionValue } = useGeoDataEffect(
    geoPostDescription,
  )
  const geoLocationTitle = useGeoDataEffect(geoTitle)

  return (
    <>
      <section
        id="hero-section"
        className={classNames(
          "relative lg:flex md:h-[600px] bg-secondary-100 mx-auto justify-center md:pt-8",
        )}
      >
        <div className="w-full md:w-1/2 lg:w-2/3 lg:max-w-[651px]">
          <div
            className={`md:h-full relative max-w-4xl lg:max-w-5xl xl:max-w-7xl mx-auto px-6 lg:px-8 pt-1 md:pt-6 pb-8 md:py-8`}
          >
            <div className="text-black font-inter max-w-3xl text-left md:mt-10 lg:mt-10 mb-0">
              <h1 className="text-[32px] leading-[46px] mb-2 md:leading-[70px] sm:text-[42px] md:text-[56px] font-bold lg:text-[56px] mt-1.5 md:mt-4 md:mb-6">
                {geoLocationTitle.geoDescription && geoLocationTitle.isUSVisitor
                  ? geoLocationTitle.geoDescription
                  : title}
              </h1>
              <p
                id="PremiumHero-description"
                className="text-[17px] leading-9 md:leading-10 max-w-lg font-inter mb-0"
              >
                {safelyRenderHtml(
                  `${description} ${
                    hasGeoCustomization ? geoDescription : ""
                  } `,
                )}
              </p>
            </div>
          </div>
        </div>
        {/* Desktop video - hidden on mobile via CSS to avoid hydration mismatch */}
        <div className="hidden md:flex w-full md:w-1/2 lg:w-1/3 items-start justify-center px-4 md:px-6">
          {embedUrl && (
            <div
              className={classNames("w-full aspect-video my-6 md:mt:5 md:pt-5")}
            >
              {/* Preload only where this copy shows; the mobile copy sits
                  below the form, so it stays lazy with no preload. */}
              <YoutubeEmbedATF
                blok={{ videoId: embedUrl, maintainAspectRatio: true }}
                posterPreloadMedia={DESKTOP_VIDEO_MEDIA}
              />
            </div>
          )}
        </div>
        <div
          className={classNames(
            "relative lg:absolute lg:-bottom-24 max-w-7xl mx-auto md:flex justify-center bg-white rounded-1 px-6 md:px-5 lg:px-8 md:py-11 right-0 left-0",
            hasListingForm ? "md:h-[300px]" : "md:h-[240px]",
          )}
        >
          <div data-testid="form-atf-5" className="py-3">
            <PremiumHeroForm
              buttonLabel={buttonLabel}
              buttonLink={buttonLink}
              hasEmail={false}
              hideLabel
              hasSwapCustomization={hasSwapCustomization}
              geoSessionValue={geoSessionValue as string}
              font="font-inter"
              customClassName={PREMIUM_HERO_FORM_ATF_LAYOUT}
            />
          </div>
        </div>
      </section>
      {/* Mobile video - hidden on desktop via CSS to avoid hydration mismatch */}
      {embedUrl && (
        <div className="block md:hidden px-6 mt-4">
          <div className="w-full min-h-[300px] mx-auto flex justify-center">
            <YoutubeEmbedATF
              blok={{ videoId: embedUrl, maintainAspectRatio: true }}
            />
          </div>
        </div>
      )}
      <section className="md:mb-10" />
    </>
  )
}

export default PetsHeroATFYoutube
